import React from 'react';
import { 
  FaPrayingHands as FaPray, 
  FaChurch, 
  FaHandsHelping, 
  FaChalkboardTeacher, 
  FaUsers, 
  FaGlobe, 
  FaHome, 
  FaLandmark, 
  FaBriefcase, 
  FaGraduationCap, 
  FaTv, 
  FaMusic,
  FaCross,
  FaHandHoldingHeart} from 'react-icons/fa';
import { motion } from 'framer-motion';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-blue-100 rounded-full p-3 mb-6">
              <FaCross className="text-blue-700 text-3xl" />
            </div>
            <h1 className="text-5xl font-bold text-blue-900 mb-6 font-serif">
              Life Missions Ministry
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bringing the light of Christ to the unreached since 2000
            </p>
          </motion.div>
        </div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509057398-6d59e188129f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')] bg-cover bg-center opacity-10"></div>
      </div>

      {/* Ministry Overview */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10"
      >
        <motion.div variants={itemVariants} className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3 bg-blue-700 p-12 flex items-center justify-center">
              <FaGlobe className="text-white text-8xl opacity-90" />
            </div>
            <div className="md:w-2/3 p-12">
              <div className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">Our Story</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 font-serif">Serving Christ in Kenya and Beyond</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Life Missions Ministry is a faith-based ministry founded in the year 2000 located in Moi's Bridge Rift Valley, 
                Trans-Nzoia County, Kenya. We are dedicated to sharing the Gospel and love of Jesus Christ by reaching the 
                unreached people through strategic missions and holistic transformation.
              </p>
              <div className="flex items-center">
                <div className="bg-blue-100 rounded-full p-3 mr-4">
                  <FaHandHoldingHeart className="text-blue-700 text-xl" />
                </div>
                <p className="text-gray-600 italic">
                  "We exist to make Jesus known among all nations"
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.section>



      {/* Strategic Areas */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="text-center mb-10"
        >
          <motion.div variants={itemVariants}>
            <h2 className="text-4xl font-bold text-blue-900 mb-6 font-serif">Our Strategic Focus Areas</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We pursue holistic transformation through these key ministry areas
            </p>
          </motion.div>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {[
            {
              icon: <FaPray className="text-blue-700 text-5xl" />,
              title: "Prayer Mobilization",
              description: "Building prayer networks and intercessory teams to cover our missions in spiritual warfare."
            },
            {
              icon: <FaChurch className="text-blue-700 text-5xl" />,
              title: "Evangelistic Missions & Church Planting",
              description: "Taking the Gospel to unreached communities and establishing Christ-centered churches."
            },
            {
              icon: <FaHandsHelping className="text-blue-700 text-5xl" />,
              title: "Discipleship Missions",
              description: "Nurturing new believers to grow in their faith and become mature followers of Christ."
            },
            {
              icon: <FaChalkboardTeacher className="text-blue-700 text-5xl" />,
              title: "Leadership Training",
              description: "Equipping local leaders with biblical knowledge and ministry skills for effective service."
            },
            {
              icon: <FaUsers className="text-blue-700 text-5xl" />,
              title: "Community Engagement",
              description: "Demonstrating Christ's love through practical impact programs that meet physical needs."
            },
            {
              icon: <FaGlobe className="text-blue-700 text-5xl" />,
              title: "Holistic Transformation",
              description: "Bringing spiritual, physical, and social transformation to entire communities."
            }
          ].map((area, index) => (
            <motion.div 
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className="bg-blue-100 p-8 flex justify-center">
                {area.icon}
              </div>
              <div className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{area.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {area.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Spheres of Transformation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.div variants={itemVariants}>
            <h2 className="text-4xl font-bold text-blue-900 mb-6 font-serif">Seven Spheres of Transformation</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe in transforming every aspect of society for Christ
            </p>
          </motion.div>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-6"
        >
          {[
            { icon: <FaChurch />, name: "Missions" },
            { icon: <FaHome />, name: "Family" },
            { icon: <FaLandmark />, name: "Government" },
            { icon: <FaBriefcase />, name: "Business" },
            { icon: <FaGraduationCap />, name: "Education" },
            { icon: <FaTv />, name: "Media" },
            { icon: <FaMusic />, name: "Music" },
          ].map((sphere, index) => (
            <motion.div 
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-blue-900 to-blue-500 rounded-xl p-6 text-center text-white shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="flex justify-center mb-4">
                <div className="bg-white bg-opacity-20 p-4 rounded-full">
                  {React.cloneElement(sphere.icon, { className: "text-white text-2xl" })}
                </div>
              </div>
              <h3 className="font-medium text-lg">{sphere.name}</h3>
            </motion.div>
          ))}
        </motion.div>
      </section>

     
    </div>
  );
};

export default About;